// Código de verificação por e-mail (Etapa 13): funções puras do painel (sem React, sem rede).
//
// O escritório encaminha os e-mails de código do tribunal para um endereço do Lictor
// (GET /orgs/me/mfa-inbox). Aqui ficam as regras de quando mostrar a configuração e os textos
// do passo a passo para criar a regra de encaminhamento (Gmail e Outlook/Microsoft 365).

import { RECURSO_MFA_EMAIL, temRecurso } from './features';

/** Só owner/admin veem a configuração, e só se a organização tem o recurso liberado (T-13.7). */
export function secaoDoCodigoPorEmailVisivel(membership: { role?: string } | null | undefined): boolean {
  const papel = membership?.role;
  return (papel === 'owner' || papel === 'admin') && temRecurso(membership, RECURSO_MFA_EMAIL);
}

/** Sistemas cujo tribunal manda o código de login por e-mail (hoje, só o e-SAJ; o PJe saiu na descoberta). */
const SISTEMAS_COM_CODIGO_POR_EMAIL = ['esaj'];

/** O campo "E-mail que recebe o código do tribunal" só faz sentido nesses sistemas e com o recurso ligado. */
export function campoDoEmailDoCodigoVisivel(
  membership: object | null | undefined,
  sistema: string | null | undefined
): boolean {
  return temRecurso(membership, RECURSO_MFA_EMAIL) && SISTEMAS_COM_CODIGO_POR_EMAIL.includes((sistema ?? '').trim().toLowerCase());
}

/** Confere só o formato (o servidor confere de novo): sem espaços, uma @, algo antes e um ponto depois. */
export function emailDoCodigoValido(valor: string): boolean {
  const texto = valor.trim();
  return texto.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(texto);
}

export interface PassoAPasso {
  titulo: string;
  /** Um passo por linha, na ordem. */
  passos: string[];
  /** Avisos de quem pode travar o encaminhamento (ex.: o TI do escritório). */
  avisos: string[];
}

export interface PassosDeEncaminhamento {
  gmail: PassoAPasso;
  outlook: PassoAPasso;
}

/** Monta o passo a passo com o endereço do escritório dentro do texto (T-13.1). */
export function montarPassoAPasso(endereco: string): PassosDeEncaminhamento {
  return {
    gmail: {
      titulo: 'Gmail',
      passos: [
        'Abra um e-mail de código que o tribunal já mandou para você e anote o endereço de quem enviou (o remetente).',
        'No Gmail, clique na engrenagem e em "Ver todas as configurações".',
        'Abra a aba "Encaminhamento e POP/IMAP" e clique em "Adicionar um endereço de encaminhamento".',
        `Digite ${endereco} e continue. O Gmail manda uma confirmação para esse endereço: o código aparece aqui, em "Confirmação do Gmail". Digite-o no Gmail para confirmar.`,
        'Agora abra a aba "Filtros e endereços bloqueados" e clique em "Criar um novo filtro".',
        'No campo "De", digite o endereço do remetente que você anotou no passo 1 e clique em "Criar filtro".',
        `Marque "Encaminhar para", escolha ${endereco} e clique em "Criar filtro".`,
      ],
      avisos: [
        'Se o seu Gmail é de empresa (Google Workspace), o administrador do domínio pode ter bloqueado o encaminhamento para fora. Se a opção não aparecer ou der erro, peça ao responsável de TI para liberar.',
      ],
    },
    outlook: {
      titulo: 'Outlook / Microsoft 365',
      passos: [
        'Abra um e-mail de código que o tribunal já mandou para você e anote o endereço de quem enviou (o remetente).',
        'No Outlook, abra as Configurações e vá em "E-mail" e depois em "Regras".',
        'Clique em "Adicionar nova regra" e dê um nome, por exemplo "Código do tribunal".',
        'Em "Adicionar uma condição", escolha "De" e digite o endereço do remetente que você anotou no passo 1.',
        `Em "Adicionar uma ação", escolha "Encaminhar para" (ou "Redirecionar para") e digite ${endereco}.`,
        'Salve a regra.',
      ],
      avisos: [
        'Atenção: em muitos escritórios o Microsoft 365 bloqueia o encaminhamento automático para fora da empresa. Se o e-mail voltar com o erro "5.7.520", o TI precisa liberar.',
        'O que o TI faz: no portal Microsoft Defender (security.microsoft.com), em "E-mail e colaboração", "Políticas e regras", "Políticas de ameaças", "Antispam", criar uma política de saída só para os advogados que vão usar, com as regras de encaminhamento automático em "Ligado".',
      ],
    },
  };
}

export interface ConfirmacaoDoGmail {
  pending: boolean;
  link: string | null;
  code: string | null;
  requested_by: string | null;
  received_at: string | null;
}

/** O que mostrar na caixa "Confirmação do Gmail". Hoje a API sempre devolve vazio (espera o teste P6). */
export function resumoDaConfirmacaoDoGmail(
  confirmacao: ConfirmacaoDoGmail | null | undefined
): { tipo: 'vazia' | 'pendente'; codigo: string | null; link: string | null; pedidoPor: string | null } {
  if (!confirmacao || !confirmacao.pending || (!confirmacao.code && !confirmacao.link)) {
    return { tipo: 'vazia', codigo: null, link: null, pedidoPor: null };
  }
  // A API só devolve https, mas o painel confere de novo antes de virar um link clicável.
  const link = confirmacao.link && /^https:\/\//i.test(confirmacao.link) ? confirmacao.link : null;
  return { tipo: 'pendente', codigo: confirmacao.code, link, pedidoPor: confirmacao.requested_by };
}

/** Mensagem para o que pode dar errado ao falar com a API da caixa de códigos. */
export function mensagemDeErroDaCaixa(status: number | undefined): string {
  if (status === 503) return 'Este recurso ainda não está configurado no servidor. Fale com o suporte do Lictor.';
  if (status === 403 || status === 404) return 'Este recurso não está disponível para você ou para a sua organização.';
  return 'Não foi possível falar com o Lictor agora. Tente novamente em instantes.';
}
